import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Truck, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Location, TractorCapability, TractorBase } from "@/types/tms";

interface ParsedTractor {
  name: string;
  capabilities: TractorCapability[];
  base_truck: TractorBase | null;
  home_base_id: string | null;
  isValid: boolean;
  error?: string;
}

const VALID_CAPABILITIES: TractorCapability[] = ["VO", "NVO", "Manual"];
const VALID_BASES: TractorBase[] = ["Peterbilt", "Volvo", "International", "Daimler"];

interface BulkTractorAddProps {
  variant?: "card" | "dialog";
  onSuccess?: () => void;
}

export function BulkTractorAdd({ variant = "card", onSuccess }: BulkTractorAddProps) {
  const queryClient = useQueryClient();
  const [rawInput, setRawInput] = useState("");
  const [defaultHomeBase, setDefaultHomeBase] = useState<string>("");
  const [parsedTractors, setParsedTractors] = useState<ParsedTractor[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { data: locations } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });

  // Pull every existing tractor name once so we can flag collisions before submit.
  // The DB now enforces UNIQUE(lower(name)) globally, so a case-insensitive set is enough.
  const { data: existingNames } = useQuery({
    queryKey: ["tractor-names-lower"],
    queryFn: async () => {
      const { data, error } = await supabase.from("tractors").select("name");
      if (error) throw error;
      return new Set((data ?? []).map((t) => (t.name ?? "").trim().toLowerCase()));
    },
  });

  const bulkAddMutation = useMutation({
    mutationFn: async (tractors: ParsedTractor[]) => {
      const validTractors = tractors.filter((t) => t.isValid);
      if (validTractors.length === 0) {
        throw new Error("No valid tractors to add");
      }

      const insertData = validTractors.map((t) => ({
        name: t.name.trim(),
        capabilities: t.capabilities,
        base_truck: t.base_truck,
        home_base_id: t.home_base_id,
      }));

      const { error } = await supabase.from("tractors").insert(insertData);
      if (error) {
        if ((error as { code?: string }).code === "23505") {
          throw new Error(
            `One or more tractors collide with an existing record (duplicate name, VIN, or Samsara ID). ${error.message}`,
          );
        }
        throw error;
      }

      return validTractors.length;
    },
    onSuccess: async (count) => {
      queryClient.invalidateQueries({ queryKey: ["tractors"] });
      queryClient.invalidateQueries({ queryKey: ["tractors-with-home-base"] });
      queryClient.invalidateQueries({ queryKey: ["tractor-names-lower"] });
      toast.success(`Successfully added ${count} tractor${count > 1 ? "s" : ""}`);
      setRawInput("");
      setParsedTractors([]);
      setShowPreview(false);
      onSuccess?.();
      // Auto-sync Samsara IDs for new tractors
      try {
        await supabase.functions.invoke("sync-samsara-ids");
        queryClient.invalidateQueries({ queryKey: ["tractors"] });
      } catch (e) {
        // Silent fail
      }
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const parseInput = () => {
    const lines = rawInput
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const parsed: ParsedTractor[] = lines.map((line) => {
      // Simple format: just the tractor name per line
      // Or CSV format: name, status, capabilities, base_truck
      const parts = line.split(",").map((p) => p.trim());
      const name = parts[0] || "";

      if (!name) {
        return {
          name: "",
          capabilities: [],
          base_truck: null,
          home_base_id: defaultHomeBase || null,
          isValid: false,
          error: "Name is required",
        };
      }

      // Check for duplicate names in current batch
      const duplicateInBatch = lines.filter(
        (l) => l.split(",")[0]?.trim().toLowerCase() === name.toLowerCase()
      ).length > 1;

      if (duplicateInBatch) {
        return {
          name,
          capabilities: [],
          base_truck: null,
          home_base_id: defaultHomeBase || null,
          isValid: false,
          error: "Duplicate name in batch",
        };
      }

      // Collision with an existing tractor (case-insensitive — matches the DB unique index).
      if (existingNames?.has(name.toLowerCase())) {
        return {
          name,
          capabilities: [],
          base_truck: null,
          home_base_id: defaultHomeBase || null,
          isValid: false,
          error: "Already exists",
        };
      }

      // Parse optional fields if provided (capability, base_truck)
      let capability: TractorCapability | null = null;
      let baseTruck: TractorBase | null = null;

      if (parts[1]) {
        const capInput = parts[1].toUpperCase().trim();
        if (VALID_CAPABILITIES.includes(capInput as TractorCapability)) {
          capability = capInput as TractorCapability;
        }
      }

      if (parts[2]) {
        const baseInput = parts[2];
        const matchedBase = VALID_BASES.find(
          (b) => b.toLowerCase() === baseInput.toLowerCase()
        );
        if (matchedBase) {
          baseTruck = matchedBase;
        }
      }

      return {
        name,
        capabilities: capability ? [capability] : [],
        base_truck: baseTruck,
        home_base_id: defaultHomeBase || null,
        isValid: true,
      };
    });

    setParsedTractors(parsed);
    setShowPreview(true);
  };

  const validCount = parsedTractors.filter((t) => t.isValid).length;
  const invalidCount = parsedTractors.filter((t) => !t.isValid).length;

  const content = (
    <div className="space-y-4">
        {/* Default settings */}
        <div className="space-y-2">
          <Label>Default Home Base</Label>
          <Select 
            value={defaultHomeBase || "__none__"} 
            onValueChange={(v) => setDefaultHomeBase(v === "__none__" ? "" : v)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select home base..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">None</SelectItem>
              {locations?.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.short_code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Input area */}
        <div className="space-y-2">
          <Label>Tractor Names (one per line)</Label>
          <Textarea
            placeholder={`T-101
T-102, VO, Peterbilt
T-103, Manual, Volvo`}
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            rows={8}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Format: name[, capability, base_truck]. Capability: VO, NVO, or Manual.
          </p>
        </div>

        {/* Parse button */}
        <Button onClick={parseInput} disabled={!rawInput.trim()} variant="outline">
          Preview Import
        </Button>

        {/* Preview section */}
        {showPreview && parsedTractors.length > 0 && (
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center gap-4">
              {validCount > 0 && (
                <div className="flex items-center gap-1 text-sm text-[hsl(var(--status-confirmed))]">
                  <CheckCircle2 className="h-4 w-4" />
                  {validCount} valid
                </div>
              )}
              {invalidCount > 0 && (
                <div className="flex items-center gap-1 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  {invalidCount} invalid
                </div>
              )}
            </div>

            {invalidCount > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {parsedTractors
                    .filter((t) => !t.isValid)
                    .map((t) => `${t.name || "(empty)"}: ${t.error}`)
                    .join("; ")}
                </AlertDescription>
              </Alert>
            )}

            <div className="max-h-[200px] overflow-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Name</th>
                    <th className="px-3 py-2 text-left font-medium">Capability</th>
                    <th className="px-3 py-2 text-left font-medium">Base Truck</th>
                    <th className="px-3 py-2 text-left font-medium">Home Base</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedTractors.map((tractor, idx) => (
                    <tr
                      key={idx}
                      className={tractor.isValid ? "" : "bg-destructive/10 text-destructive"}
                    >
                      <td className="px-3 py-2 font-medium">{tractor.name || "—"}</td>
                      <td className="px-3 py-2">
                        {tractor.capabilities[0] || "—"}
                      </td>
                      <td className="px-3 py-2">{tractor.base_truck || "—"}</td>
                      <td className="px-3 py-2">
                        {locations?.find((l) => l.id === tractor.home_base_id)?.short_code || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => bulkAddMutation.mutate(parsedTractors)}
                disabled={validCount === 0 || bulkAddMutation.isPending}
              >
                <Upload className="h-4 w-4 mr-2" />
                Add {validCount} Tractor{validCount !== 1 ? "s" : ""}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowPreview(false);
                  setParsedTractors([]);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
    </div>
  );

  if (variant === "dialog") {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Bulk Add Tractors
        </CardTitle>
        <CardDescription>
          Add multiple tractors at once. Enter one tractor per line, or use CSV format: 
          name, capability, base_truck
        </CardDescription>
      </CardHeader>
      <CardContent>{content}</CardContent>
    </Card>
  );
}
